import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { IconButton } from "@mui/material";

// Define color styles outside component to avoid recreation
const COLOR_STYLES = {
  blue: {
    color: "primary.60",
    "&:hover": {
      backgroundColor: "primary.20",
      color: "primary.80"
    },
    "&:focus": {
      backgroundColor: "primary.20",
      color: "primary.80"
    },
    "&:active": {
      backgroundColor: "primary.40",
      color: "primary.80",
    },
    "&:disabled": {
      color: "primary.30",
    },
  },
  white: {
    color: "white_opacity.100",
    "&:hover": {
      backgroundColor: "white_opacity.30",
      color: "white_opacity.100",
    },
    "&:focus": {
      backgroundColor: "white_opacity.30",
      color: "white_opacity.100",
    },
    "&:active": {
      backgroundColor: "white_opacity.40",
      color: "white_opacity.100",
    },
    "&:disabled": {
      color: "white_opacity.80",
    },
  },
  grey: {
    color: "black_solid.60",
    "&:hover": {
      backgroundColor: "black_opacity.20",
      color: "black_solid.80",
    },
    "&:focus": {
      backgroundColor: "black_opacity.20",
      color: "black_solid.80",
    },
    "&:active": {
      backgroundColor: "black_opacity.40",
      color: "black_solid.80",
    },
    "&:disabled": {
      color: "black_opacity.60",
    },
  },
  red: {
    color: "error.60",
    "&:hover": {
      backgroundColor: "error.20",
      color: "error.80",
    },
    "&:focus": {
      backgroundColor: "error.20",
      color: "error.80",
    },
    "&:active": {
      backgroundColor: "error.40",
      color: "error.80",
    },
    "&:disabled": {
      color: "error.60",
    },
  },
  orange: {
    color: "orange.60",
    "&:hover": {
      backgroundColor: "orange.20",
      color: "orange.80",
    },
    "&:focus": {
      backgroundColor: "orange.20",
      color: "orange.80",
    },
    "&:active": {
      backgroundColor: "orange.40",
      color: "orange.80",
    },
    "&:disabled": {
      color: "orange.30",
    },
  }
};

// Base styles that don't change
const BASE_STYLES = {
  textTransform: "none",
  borderRadius: "3px",
  padding: "2px 2px",
};

const InlineIconButton = React.memo(({
  id,
  onClick,
  disabled,
  size,
  icon,
  buttonColor
}) => {
  const Icon = icon;

  // Memoize the complete styles object
  const buttonStyles = useMemo(() => {
    return {
      ...BASE_STYLES,
      ...(COLOR_STYLES[buttonColor] || COLOR_STYLES.blue)
    };
  }, [buttonColor]);

  // Memoize icon styles
  const iconStyles = useMemo(() => ({
    fontSize: size === "small" ? "16px" : "20px"
  }), [size]);

  return (
    <IconButton
      id={id}
      onClick={onClick}
      disabled={disabled}
      sx={buttonStyles}
    >
      <Icon sx={iconStyles} />
    </IconButton>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - return true if props are equal (skip re-render)
  return (
    prevProps.id === nextProps.id &&
    prevProps.onClick === nextProps.onClick &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.size === nextProps.size &&
    prevProps.icon === nextProps.icon &&
    prevProps.buttonColor === nextProps.buttonColor
  );
});

InlineIconButton.displayName = 'InlineIconButton';

InlineIconButton.propTypes = {
  id: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  size: PropTypes.string,
  icon: PropTypes.any.isRequired,
  buttonColor: PropTypes.string
};

export default InlineIconButton;